import { DueBadge, Pill, daysUntil, fmtDate } from '../primitives';
import { Actions, CrossLink, KeyValues, Note, Section, Steps, type KV, type Step } from './Drawer';
import type { Connector, ProgramResponse } from '../../lib/types';

export type DrawerTarget =
  | { kind: 'control'; id: string }
  | { kind: 'deadline'; id: string }
  | { kind: 'incident'; id: string }
  | { kind: 'request'; id: string }
  | { kind: 'vendor'; id: string }
  | { kind: 'connector'; id: string };

export interface DrawerContent {
  eyebrow: string;
  title: string;
  meta?: React.ReactNode;
  body: React.ReactNode;
  footer?: React.ReactNode;
}

const who = (ref: string | null, analysts: Record<string, string>) =>
  ref ? (analysts[ref] ?? ref) : null;

const CONTROL_STATUS: Record<string, string> = {
  met: 'Met',
  gap: 'Gap',
  in_progress: 'In progress',
  unassessed: 'Not assessed',
};

export function buildDrawer(
  target: DrawerTarget,
  program: ProgramResponse,
  connectors: Connector[],
  open: (t: DrawerTarget) => void,
  goto: (path: string) => void,
): DrawerContent | null {
  const { analysts } = program;

  // ── Control ───────────────────────────────────────────────────────────────
  if (target.kind === 'control') {
    const c = program.controls.find((x) => x.id === target.id);
    if (!c) return null;
    // A control's calendar items are the things that close it. This is the
    // cross-link the handoff calls the point of the module.
    const linked = program.deadlines.filter((d) => d.control_id === c.id);
    const evidenceAge = c.evidence_collected_at ? daysUntil(c.evidence_collected_at) : null;

    const kv: KV[] = [
      ['Framework', c.framework],
      ['Scope', c.scope],
      ['Owner', who(c.owner_ref, analysts)],
      ['Evidence', c.evidence_artifact],
      [
        'Evidence age',
        c.evidence_collected_at
          ? `${Math.abs(evidenceAge ?? 0)} d old`
          : c.status === 'in_progress'
            ? 'collecting now'
            : null,
      ],
      ['Next review', c.next_review ? <DueBadge date={c.next_review} /> : null],
    ];

    return {
      eyebrow: `${c.framework} · ${c.ref}`,
      title: c.name,
      meta: (
        <>
          <Pill kind={c.status === 'met' ? 'ok' : c.status === 'gap' ? 'bad' : c.status === 'in_progress' ? 'info' : 'neutral'}>
            {CONTROL_STATUS[c.status] ?? c.status}
          </Pill>
          <Pill kind="neutral">{c.framework}</Pill>
        </>
      ),
      body: (
        <>
          {c.finding && (
            <Section label="Open finding">
              <Note danger>{c.finding}</Note>
            </Section>
          )}
          <Section label="Control">
            <KeyValues rows={kv} />
          </Section>
          {c.linked_detection_ref && (
            <Section label="Evidenced by">
              {/* A detection ref, not a link: detections live in the connector,
                  and no connector exposes a per-detection feed yet. Showing the
                  ref is honest; linking to a page that cannot load is not. */}
              <Note>
                Detection <b>{c.linked_detection_ref}</b> evidences this gap. The source connector is
                not reporting, so the detection itself cannot be opened.
              </Note>
            </Section>
          )}
          <Section label={`Calendar items — ${linked.length}`}>
            {linked.length === 0 ? (
              <Note>No calendar item is scheduled against this control.</Note>
            ) : (
              linked.map((d) => (
                <CrossLink
                  key={d.id}
                  tag={d.ref}
                  label={`${d.title} · ${fmtDate(d.due)}`}
                  onOpen={() => open({ kind: 'deadline', id: d.id })}
                />
              ))
            )}
          </Section>
        </>
      ),
      footer: <Actions labels={['Request evidence', 'Mark tested']} />,
    };
  }

  // ── Deadline ──────────────────────────────────────────────────────────────
  if (target.kind === 'deadline') {
    const d = program.deadlines.find((x) => x.id === target.id);
    if (!d) return null;
    const control = program.controls.find((c) => c.id === d.control_id);

    return {
      eyebrow: `${d.type}${d.framework ? ` · ${d.framework}` : ''} · ${d.ref}`,
      title: d.title,
      meta: (
        <>
          <Pill kind={(daysUntil(d.due) ?? 0) < 0 ? 'bad' : 'info'}>{d.status}</Pill>
          {d.framework && <Pill kind="neutral">{d.framework}</Pill>}
        </>
      ),
      body: (
        <>
          <Section label="Item">
            <KeyValues
              rows={[
                ['Type', d.type],
                ['Framework', d.framework],
                ['Owner', who(d.owner_ref, analysts)],
                ['Due', <DueBadge key="d" date={d.due} />],
                ['Status', d.status],
                ['Linked control', control ? `${control.ref} — ${control.name}` : null],
              ]}
            />
          </Section>
          {control && (
            <Section label="Control status">
              <Note danger={control.status === 'gap'}>
                {control.ref} is currently <b>{CONTROL_STATUS[control.status] ?? control.status}</b>
                {control.finding ? `. ${control.finding}` : '.'}
              </Note>
              <CrossLink
                tag={control.ref}
                label={control.name}
                onOpen={() => open({ kind: 'control', id: control.id })}
              />
            </Section>
          )}
        </>
      ),
      footer: <Actions labels={['Reassign', 'Attach evidence', 'Mark complete']} />,
    };
  }

  // ── Incident ──────────────────────────────────────────────────────────────
  if (target.kind === 'incident') {
    const i = program.incidents.find((x) => x.id === target.id);
    if (!i) return null;

    // Lifecycle is fixed: Triage → Containment → Remediation → Recovery →
    // Closed. Rendering it as a timeline shows where the incident actually is
    // rather than making the reader infer it from a status word.
    const LIFECYCLE = ['Triage', 'Containment', 'Remediation', 'Recovery', 'Closed'];
    const at = LIFECYCLE.indexOf(i.status);
    const steps: Step[] = LIFECYCLE.map((name, idx) => ({
      title: name,
      state: at < 0 ? 'future' : idx < at ? 'done' : idx === at ? 'now' : 'future',
    }));

    return {
      eyebrow: `${i.source ?? 'Incident'} · ${i.ref}`,
      title: i.title,
      meta: (
        <>
          <Pill kind={i.severity === 'critical' ? 'bad' : i.severity === 'low' ? 'neutral' : 'warn'}>
            {i.severity}
          </Pill>
          <Pill kind={i.status === 'Closed' ? 'ok' : i.status === 'Triage' ? 'warn' : 'info'}>
            {i.status}
          </Pill>
        </>
      ),
      body: (
        <>
          {i.summary && (
            <Section label="Summary">
              <Note>{i.summary}</Note>
            </Section>
          )}
          <Section label="Detail">
            <KeyValues
              rows={[
                ['Opened', fmtDate(i.opened)],
                ['Closed', i.closed ? fmtDate(i.closed) : null],
                ['Lead analyst', who(i.analyst_ref, analysts)],
                ['Source', i.source],
                ['Source detection', i.source_detection_ref],
              ]}
            />
          </Section>
          <Section label="Lifecycle">
            <Steps steps={steps} />
          </Section>
        </>
      ),
      footer: <Actions labels={['Add note', 'Notify Legal', 'Close incident']} />,
    };
  }

  // ── Request ───────────────────────────────────────────────────────────────
  if (target.kind === 'request') {
    const r = program.requests.find((x) => x.id === target.id);
    if (!r) return null;
    const closed = ['Closed', 'Approved', 'Denied'].includes(r.status);

    return {
      eyebrow: `${r.kind} · ${r.ref}`,
      title: r.title,
      meta: (
        <>
          <Pill kind={r.kind === 'Exception' ? 'violet' : r.kind === 'Vendor review' ? 'teal' : 'neutral'}>
            {r.kind}
          </Pill>
          <Pill kind={r.status === 'Overdue' ? 'bad' : closed ? 'ok' : 'info'}>{r.status}</Pill>
        </>
      ),
      body: (
        <>
          <Section label="Request">
            <KeyValues
              rows={[
                ['Requester', r.requester],
                ['Department', r.department],
                ['Opened', fmtDate(r.opened)],
                ['SLA', r.sla_due ? <DueBadge key="s" date={r.sla_due} /> : null],
                ['Priority', r.priority],
                ['Linked signal', r.linked_signal_ref],
              ]}
            />
          </Section>
          {r.kind === 'Exception' && (
            <Section label="Exception expiry">
              {/* An exception without an expiry is granted forever by accident —
                  the most common way a compliance program quietly rots. */}
              <Note danger={!r.expires_at}>
                {r.expires_at
                  ? `This exception lapses on ${fmtDate(r.expires_at)} and must be back on the calendar before then.`
                  : 'No expiry is recorded. An exception with no end date is a permanent gap that nobody will revisit — set one.'}
              </Note>
            </Section>
          )}
        </>
      ),
      footer: <Actions labels={['Reply in Teams', 'Deny', 'Approve']} />,
    };
  }

  // ── Vendor ────────────────────────────────────────────────────────────────
  if (target.kind === 'vendor') {
    const v = program.vendors.find((x) => x.id === target.id);
    if (!v) return null;

    // The four gates that must clear before ContractWISE may accept a
    // signature. Rendered as a timeline so the blocking one is obvious.
    const gates: Step[] = [
      { title: 'Questionnaire returned', state: v.gate_questionnaire ? 'done' : 'now' },
      { title: 'Attestation reviewed', state: v.gate_attestation ? 'done' : v.gate_questionnaire ? 'now' : 'future' },
      {
        title: 'CPRA service-provider terms in contract',
        state: v.gate_cpra_terms ? 'done' : v.gate_attestation ? 'now' : 'future',
      },
      {
        title: 'Disposition recorded',
        state: v.gate_disposition ? 'done' : v.gate_cpra_terms ? 'now' : 'future',
      },
    ];
    const cleared = gates.filter((g) => g.state === 'done').length;

    return {
      eyebrow: `Vendor risk · ${v.category ?? 'uncategorised'}`,
      title: v.name,
      meta: (
        <>
          <Pill kind={v.review_status === 'Approved' ? 'ok' : v.review_status === 'In review' ? 'info' : 'bad'}>
            {v.review_status}
          </Pill>
          {v.inherent_risk && (
            <Pill kind={v.inherent_risk === 'High' ? 'bad' : 'neutral'}>{v.inherent_risk} inherent</Pill>
          )}
        </>
      ),
      body: (
        <>
          {v.review_status === 'Live without review' && (
            <Section label="Exposure">
              <Note danger>
                This vendor is live without a completed review — the SOC 2 CC9.2 exposure. Contract
                signature should be blocked in ContractWISE until a disposition is recorded.
              </Note>
            </Section>
          )}
          <Section label="Assessment">
            <KeyValues
              rows={[
                ['Category', v.category],
                ['Inherent risk', v.inherent_risk],
                ['Data handled', v.data_handled],
                ['Attestation', v.attestation],
                ['Owner', who(v.owner_ref, analysts)],
                ['Next action', v.next_action_due ? <DueBadge key="n" date={v.next_action_due} /> : null],
              ]}
            />
          </Section>
          <Section label={`Required before signature — ${cleared} of 4`}>
            <Steps steps={gates} />
          </Section>
        </>
      ),
      footer: <Actions labels={['Send questionnaire', 'Record disposition']} />,
    };
  }

  // ── Connector ─────────────────────────────────────────────────────────────
  const c = connectors.find((x) => x.id === target.id);
  if (!c) return null;

  return {
    eyebrow: `${c.vendor} · connector`,
    title: c.name,
    meta: (
      <Pill kind={c.state === 'healthy' ? 'ok' : c.state === 'degraded' ? 'warn' : 'neutral'}>
        {c.state === 'not_connected' ? 'not connected' : c.state}
      </Pill>
    ),
    body: (
      <>
        <Section label="Coverage">
          {c.state === 'not_connected' ? (
            <Note danger>{c.reason}</Note>
          ) : c.state === 'degraded' ? (
            <Note danger>{c.reason}</Note>
          ) : (
            <Note>{c.coverage ?? 'Coverage is not reported by this connector.'}</Note>
          )}
        </Section>

        {c.metrics.length > 0 && (
          <Section label="Live metrics">
            <KeyValues
              rows={c.metrics.map((m) => [
                m.l,
                m.v === null ? <span className="cw-unread">unreadable</span> : String(m.v),
              ])}
            />
          </Section>
        )}

        {/* Why each "unreadable" cell is unreadable, in the vendor's words —
            usually a permission not yet granted. */}
        {c.notes.length > 0 && (
          <Section label="Could not read">
            {c.notes.map((n) => (
              <Note key={n} danger>
                {n}
              </Note>
            ))}
          </Section>
        )}

        <Section label="Detections">
          {/* Deliberately not an empty lane: an empty list here would read as
              "this tool has raised nothing", when the truth is we cannot ask. */}
          <Note>
            {c.state === 'healthy'
              ? 'This connector does not expose a per-detection feed yet, so no detections can be listed.'
              : 'No detections can be listed while this connector is not reporting.'}
          </Note>
        </Section>
      </>
    ),
    footer: <Actions labels={['View in vendor console', 'Force sync']} />,
  };
}

export { };
